import { memo, useState } from 'react';
import classNames from 'classnames';
import { DateTimeSecFormat, TimeFormat, formatDate, parseDate } from '@cyren/common-lib';
import DatePickerMobile from '../../../../../common/date-picker/DatePickerMobile';
import { timeConfigPreset } from '../../../../../common/date-picker/date-picker-config';
import { V2QsTimeProps } from './types';

/**
 * V2QsTime 컴포넌트
 *
 * 시간 선택을 위한 컴포넌트
 * V1 QsInputTime의 V2 버전으로 순수 UI만 담당
 *
 * @example
 * ```tsx
 * <V2QsTime
 *   value={time}
 *   onChange={setTime}
 *   placeholder="Select time"
 * />
 * ```
 */
function V2QsTime({
  value,
  onChange,
  placeholder = 'Select time',
  error = false,
  errorMessage,
  disabled = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  confirmText = 'OK',
  cancelText = 'Cancel',
}: V2QsTimeProps) {
  const [isOpen, setIsOpen] = useState(false);

  // value를 Date 객체로 파싱
  const selectedDate = value ? parseDate(value, DateTimeSecFormat) || null : null;

  // 시간 선택 핸들러
  const handleSelect = (date: Date) => {
    setIsOpen(false);
    onChange?.(formatDate(date));
  };

  // 취소 핸들러
  const handleCancel = () => {
    setIsOpen(false);
  };

  // 버튼 클릭 핸들러
  const handleClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const hasError = error;

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // 버튼 클래스
  const buttonClasses = classNames(
    'flex items-center justify-center transition-all duration-200 rounded-[10px]',
    'text-[15px] font-medium leading-[1.6]',
    {
      // Width
      'w-full': fullWidth,
      'w-[260px]': !fullWidth,

      // Padding
      'px-5 py-[17px]': true,

      // Background
      'bg-white': !disabled,
      'bg-[#F2F4FC]': disabled,

      // Border
      'border-[1.5px]': true,
      'border-[#E6E9F5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'hover:border-[#DADDEB]': !disabled && !hasError,

      // Text color
      'text-[#1D1D1F]': selectedDate && !disabled,
      'text-[#AFB2C2]': !selectedDate || disabled,

      // Cursor
      'cursor-pointer': !disabled,
      'cursor-not-allowed': disabled,
    },
    className
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // 표시할 텍스트 (시간 포맷)
  const displayText = selectedDate
    ? formatDate(selectedDate, { formatStr: TimeFormat })
    : placeholder;

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Time Button */}
      <button
        type="button"
        className={buttonClasses}
        onClick={handleClick}
        disabled={disabled}
      >
        {/* Clock Icon */}
        <svg
          className="w-5 h-5 mr-2 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>{displayText}</span>
      </button>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}

      {/* Time Picker Modal */}
      {isOpen && (
        <DatePickerMobile
          dateConfig={timeConfigPreset}
          isOpen={isOpen}
          value={selectedDate || new Date()}
          showHeader={false}
          showCaption
          onCancel={handleCancel}
          onSelect={handleSelect}
          confirmText={confirmText}
          cancelText={cancelText}
        />
      )}
    </div>
  );
}

export default memo(V2QsTime);
